/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Stack,
    Paper,
    Box,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface KeyResult {
    keyResult: string;
    endDate: string;
}

interface Objective {
    objective: string;
    score: string;
    relatedParams: KeyResult[];
}

interface ValidationError {
    objectiveIndex?: number;
    keyResultIndex?: number;
    field: string;
    message: string;
}

interface GoalModalProps {
    open: boolean;
    onClose: () => void;
    mode: 'create' | 'edit';
    employeeName: string;
    goalsData?: Objective[] | null;
    onSubmit: (goals: Objective[]) => void;
    goalStartDate: Date | null;
    goalEndDate: Date | null;
    setGoalStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
    setGoalEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
    submitLoading?: boolean;
}

export interface GoalModalHandle {
    resetObjectives: () => void;
  }

  const GoalModal = forwardRef<GoalModalHandle, GoalModalProps>(({
    open,
    onClose,
    mode,
    employeeName,
    goalsData,
    onSubmit,
    goalStartDate,
    goalEndDate,
    setGoalStartDate,
    setGoalEndDate,
    submitLoading = false,
    
}, ref) => {
        // Create initial objective structure
        const createInitialObjective = useCallback((): Objective => ({
            objective: '',
            score: '',
            relatedParams: [{
                keyResult: '',
                endDate: '',
            }],
        }), []);

    // State management
    const [objectives, setObjectives] = useState<Objective[]>([createInitialObjective()]);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [deleteObjective, setDeleteObjective] = useState<number>(-1);

    useImperativeHandle(ref, () => ({
        resetObjectives: () => {
          setObjectives([createInitialObjective()]);
        },
      }));
    
    // Initialize objectives based on mode
    useEffect(() => {
        if (mode === 'edit' && goalsData && Array.isArray(goalsData)) {
            // Validate and sanitize incoming data
            const sanitizedObjectives = goalsData.map(obj => ({
                objective: obj.objective || '',
                score: obj.score?.toString() || '',
                relatedParams: Array.isArray(obj.relatedParams) ? obj.relatedParams.map(kr => ({
                    keyResult: kr.keyResult || '',
                    endDate: kr.endDate ? new Date(kr.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                })) : [{
                    keyResult: '',
                    endDate: new Date().toISOString().split('T')[0],
                }],
            }));
            setObjectives(sanitizedObjectives);
        } 
    }, [mode, goalsData, open]);

    useEffect(() => {
        if(open){
            setValidationErrors([]);
            setHasSubmitted(false);

        }
        if(!open && mode === 'edit'){
            setObjectives([createInitialObjective()]);
            setGoalStartDate(null);
            setGoalEndDate(null);
        }
    }, [open, createInitialObjective, mode]);

    // Validation functions
    const validateObjectives = useCallback((objectivesToValidate: Objective[]): ValidationError[] => {
        const errors: ValidationError[] = [];

        if (!Array.isArray(objectivesToValidate) || objectivesToValidate.length === 0) {
            errors.push({ field: 'objectives', message: 'At least one objective is required' });
            return errors;
        }

        objectivesToValidate.forEach((objective, objectiveIndex) => {
            // Validate objective
            if (!objective.objective || objective.objective.trim().length === 0) {
                errors.push({
                    objectiveIndex,
                    field: 'objective',
                    message: 'Objective description is required',
                });
            }

            // Validate objective score
            if (!objective.score || objective.score.trim().length === 0) {
                errors.push({
                    objectiveIndex,
                    field: 'score',
                    message: 'Score is required',
                });
            } else {
                const score = parseFloat(objective.score);
                if (isNaN(score) || score < 0 || score > 100) {
                    errors.push({
                        objectiveIndex,
                        field: 'score',
                        message: 'Score must be a number between 0 and 100',
                    });
                }
            }

            // Validate key results
            if (!Array.isArray(objective.relatedParams) || objective.relatedParams.length === 0) {
                errors.push({
                    objectiveIndex,
                    field: 'keyResults',
                    message: 'At least one key result is required',
                });
            } else {
                objective.relatedParams.forEach((keyResult, keyResultIndex) => {
                    if (!keyResult.keyResult || keyResult.keyResult.trim().length === 0) {
                        errors.push({
                            objectiveIndex,
                            keyResultIndex,
                            field: 'keyResult',
                            message: 'Key result description is required',
                        });
                    }

                    if (!keyResult.endDate) {
                        errors.push({
                            objectiveIndex,
                            keyResultIndex,
                            field: 'endDate',
                            message: 'End date is required',
                        });
                    } else {
                        const endDate = new Date(keyResult.endDate);
                        const startDate = new Date(goalStartDate || '');
                        const goalEnd = new Date(goalEndDate || '');
                        
                        if (endDate < startDate) {
                            errors.push({
                                objectiveIndex,
                                keyResultIndex,
                                field: 'endDate',
                                message: 'End date cannot be before goal start date',
                            });
                        } else if (endDate > goalEnd) {
                            errors.push({
                                objectiveIndex,
                                keyResultIndex,
                                field: 'endDate',
                                message: 'End date cannot be after goal end date',
                            });
                        }
                    }
                });
            }
        });

        return errors;
    }, [goalStartDate, goalEndDate]);

    // Check if specific field has error (only show after submit)
    const hasFieldError = useCallback((objectiveIndex: number, keyResultIndex?: number, field?: string): boolean => {
        if (!hasSubmitted) return false;
        return validationErrors.some(error => 
            error.objectiveIndex === objectiveIndex &&
            error.keyResultIndex === keyResultIndex &&
            (!field || error.field === field)
        );
    }, [validationErrors, hasSubmitted]);

    // Get error message for specific field (only show after submit)
    const getFieldError = useCallback((objectiveIndex: number, keyResultIndex?: number, field?: string): string => {
        if (!hasSubmitted) return '';
        const error = validationErrors.find(error => 
            error.objectiveIndex === objectiveIndex &&
            error.keyResultIndex === keyResultIndex &&
            (!field || error.field === field)
        );
        return error?.message || '';
    }, [validationErrors, hasSubmitted]);

    // Update objectives without immediate validation
    const updateObjectives = useCallback((newObjectives: Objective[]) => {
        setObjectives(newObjectives);
        // Only validate if form has been submitted before
        if (hasSubmitted) {
            const errors = validateObjectives(newObjectives);
            setValidationErrors(errors);
        }
    }, [validateObjectives, hasSubmitted]);

    // Add functions with validation
    const addObjective = useCallback(() => {
        const newObjectives = [...objectives, createInitialObjective()];
        updateObjectives(newObjectives);
    }, [objectives, createInitialObjective, updateObjectives]);

    const addKeyResult = useCallback((objectiveIndex: number) => {
        if (objectiveIndex < 0 || objectiveIndex >= objectives.length) return;
        
        const newObjectives = [...objectives];
        newObjectives[objectiveIndex].relatedParams.push({
            keyResult: '',
            endDate: '',
        });
        updateObjectives(newObjectives);
    }, [objectives, updateObjectives]);

    // Change handlers with validation
    const handleObjectiveChange = useCallback((objectiveIndex: number, value: string) => {
        if (objectiveIndex < 0 || objectiveIndex >= objectives.length) return;
        
        const newObjectives = [...objectives];
        newObjectives[objectiveIndex].objective = value;
        updateObjectives(newObjectives);
    }, [objectives, updateObjectives]);

    const handleObjectiveScoreChange = useCallback((objectiveIndex: number, value: string) => {
        if (objectiveIndex < 0 || objectiveIndex >= objectives.length) return;
        const newObjectives = [...objectives];
        newObjectives[objectiveIndex].score = value;
        updateObjectives(newObjectives);
    }, [objectives, updateObjectives]);

    const handleKeyResultChange = useCallback((
        objectiveIndex: number,
        keyResultIndex: number,
        field: keyof KeyResult,
        value: string
    ) => {
        if (objectiveIndex < 0 || objectiveIndex >= objectives.length ||
            keyResultIndex < 0 || keyResultIndex >= objectives[objectiveIndex].relatedParams.length) return;
        
        const newObjectives = [...objectives];
        newObjectives[objectiveIndex].relatedParams[keyResultIndex][field] = value;
        updateObjectives(newObjectives);
    }, [objectives, updateObjectives]);

    // Remove functions with validation
    const removeObjective = useCallback((objectiveIndex: number) => {
        if (objectives.length <= 1 || objectiveIndex < 0 || objectiveIndex >= objectives.length) return;
        
        const newObjectives = objectives.filter((_, index) => index !== objectiveIndex);
        updateObjectives(newObjectives);
        setDeleteObjective(-1);
    }, [objectives, updateObjectives]);

    const removeKeyResult = useCallback((objectiveIndex: number, keyResultIndex: number) => {
        if (objectiveIndex < 0 || objectiveIndex >= objectives.length ||
            objectives[objectiveIndex].relatedParams.length <= 1 ||
            keyResultIndex < 0 || keyResultIndex >= objectives[objectiveIndex].relatedParams.length) return;
        
        const newObjectives = [...objectives];
        newObjectives[objectiveIndex].relatedParams =
            newObjectives[objectiveIndex].relatedParams.filter((_, index) => index !== keyResultIndex);
        updateObjectives(newObjectives);
    }, [objectives, updateObjectives]);

    // Form validation
    const isFormValid = useMemo((): boolean => {
        return validationErrors.length === 0 && objectives.length > 0;
    }, [validationErrors, objectives]);

    // Event handlers
    const handleCancel = useCallback(() => {
        onClose();
        setValidationErrors([]);
        setHasSubmitted(false);
    }, [onClose, createInitialObjective]);

    const handleSubmit = useCallback(() => {
        setHasSubmitted(true);
        const errors = validateObjectives(objectives);
        setValidationErrors(errors);
        
        if (errors.length === 0) {
            onSubmit(objectives);
        }
    }, [objectives, validateObjectives, onSubmit]);

    // Date validation
    const isDateValid = useMemo(() => {
        if (!goalStartDate || !goalEndDate) return true;
        return goalStartDate <= goalEndDate;
    }, [goalStartDate, goalEndDate]);

    const handleDateChange = useCallback((type: 'start' | 'end', value: string) => {
        const newDate = new Date(value);
        if (type === 'start') {
            setGoalStartDate(newDate);
        } else {
            setGoalEndDate(newDate);
        }
        // Re-validate objectives when dates change (only if form was submitted)
        if (objectives.length > 0 && hasSubmitted) {
            const errors = validateObjectives(objectives);
            setValidationErrors(errors);
        }
    }, [objectives, validateObjectives, hasSubmitted, setGoalStartDate, setGoalEndDate]);

    return (
        <Dialog 
            open={open} 
            onClose={handleCancel} 
            fullWidth 
            maxWidth="xl"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
                    <Typography variant="h6" noWrap>
                        {mode === 'edit' ? 'Update' : 'Create'} Goals for {employeeName}
                    </Typography>
                    <Stack spacing={2} direction="row">
                        <TextField
                            label="Goal Start Date"
                            type="date"
                            value={ goalStartDate?.toISOString().split('T')[0] || ''}
                            onChange={(e) => handleDateChange('start', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            error={!isDateValid}
                            helperText={!isDateValid ? 'Start date must be before end date' : ''}
                        />
                        <TextField
                            label="Goal End Date"
                            type="date"
                            value={goalEndDate?.toISOString().split('T')[0] || ''}
                            onChange={(e) => handleDateChange('end', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            error={!isDateValid}
                            helperText={!isDateValid ? 'End date must be after start date' : ''}
                        />
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Box sx={{ width: '100%' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<AddOutlinedIcon />}
                        onClick={addObjective}
                        sx={{ mb: 2 }}
                    >
                        Add Objective
                    </Button>

                    <Grid container spacing={2}>
                        {objectives.map((objective, objectiveIndex) => (
                            <Grid item xs={12} key={objectiveIndex}>
                                <Paper elevation={4} sx={{ p: 2, borderRadius: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="h6">
                                            Objective {objectiveIndex + 1}
                                        </Typography>
                                        <Tooltip title={ objectives.length === 1 ? 'Atleast one objective is required' : ''} arrow>
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<DeleteOutlineIcon />}
                                                    onClick={() => setDeleteObjective(objectiveIndex)}
                                                    disabled={objectives.length === 1}
                                                >
                                                    Remove Objective
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>

                                    <Stack spacing={2} mb={2} direction="row">
                                        <TextField
                                            label="Objective"
                                            value={objective.objective}
                                            onChange={(e) => handleObjectiveChange(objectiveIndex, e.target.value)}
                                            fullWidth
                                            size="small"
                                            error={hasFieldError(objectiveIndex, undefined, 'objective')}
                                            helperText={getFieldError(objectiveIndex, undefined, 'objective')}
                                        />
                                        <TextField
                                            label="Weightage (0-100)"
                                            type="number"
                                            value={objective.score}
                                            onChange={(e) => handleObjectiveScoreChange(objectiveIndex, e.target.value)}
                                            sx={{minWidth:'290px'}}
                                            size="small"
                                            inputProps={{ min: 0, max: 100 }}
                                            error={hasFieldError(objectiveIndex, undefined, 'score')}
                                            helperText={getFieldError(objectiveIndex, undefined, 'score')}
                                        />
                                    </Stack>

                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        startIcon={<AddOutlinedIcon />}
                                        onClick={() => addKeyResult(objectiveIndex)}
                                        sx={{ mb: 2 }}
                                    >
                                        Add Key Result
                                    </Button>

                                    <Grid container spacing={2}>
                                        {objective.relatedParams.map((keyResult, keyResultIndex) => (
                                            <Grid item xs={12} key={keyResultIndex}>
                                                <Box >

                                                    <Stack spacing={2} direction="row" alignItems={'center'}>
                                                    <TextField
                                                        label="Key Result"
                                                        value={keyResult.keyResult}
                                                        onChange={(e) =>
                                                            handleKeyResultChange(objectiveIndex, keyResultIndex, 'keyResult', e.target.value)
                                                        }
                                                        fullWidth
                                                        size="small"
                                                        multiline
                                                        minRows={1}
                                                        maxRows={6}
                                                        error={hasFieldError(objectiveIndex, keyResultIndex, 'keyResult')}
                                                        helperText={getFieldError(objectiveIndex, keyResultIndex, 'keyResult')}
                                                        />

                                                        <Stack direction="row" width={'350px'}>
                                                        <TextField
                                                            label="End Date"
                                                            type="date"
                                                            value={keyResult.endDate || ''}
                                                            onChange={(e) => handleKeyResultChange(objectiveIndex, keyResultIndex, 'endDate', e.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            InputLabelProps={{ shrink: true }}
                                                            error={hasFieldError(objectiveIndex, keyResultIndex, 'endDate')}
                                                            helperText={getFieldError(objectiveIndex, keyResultIndex, 'endDate')}
                                                        />
                                                       <Tooltip title={ objective.relatedParams.length === 1 ? 'Atleast one key result is required' : ''} arrow>
                                                            <span>
                                                                <Button
                                                                    disabled={objective.relatedParams.length === 1}
                                                                    color="error"
                                                                    size="small"
                                                                    onClick={() => removeKeyResult(objectiveIndex, keyResultIndex)}
                                                                    sx={{ minWidth: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                                                >
                                                                    <DeleteOutlineIcon sx={{ fontSize: 24 }} />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <Dialog
					open={deleteObjective >= 0}
					onClose={() => setDeleteObjective(-1)}
				>
					<DialogTitle>
						Are you sure ?
					</DialogTitle>
					<DialogContent>
						Are you sure you want to remove this entry?
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => removeObjective(deleteObjective)}
							size="small"
							variant="contained"
							color="secondary"
						>
							Remove
						</Button>
					</DialogActions>
				</Dialog>
            </DialogContent>

            <DialogActions>
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%' }}>
                    <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button 
                        size="small" 
                        disabled={!isFormValid || !isDateValid || submitLoading} 
                        variant="contained" 
                        color="secondary" 
                        onClick={handleSubmit}
                        endIcon={submitLoading ? (<CircularProgress color="inherit" style={{ width: '16px', height: '16px' }} />) : null}
                    >
                        {(mode === 'edit' ? 'Update' : 'Create')}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
});

GoalModal.displayName = 'GoalModal';

export default GoalModal;